class CartPage {
  constructor() {
    this.cartPage = document.querySelector(".cart-page");
    this.closeBtn = this.cartPage?.querySelector(".cart-page__close");
    this.isUpdatingFromPage = false;
    this.isUpdatingFromTrigger = false;

    this.init();
  }

  init() {
    if (!this.cartPage) return;

    // Close button (if it exists)
    this.closeBtn?.addEventListener("click", () => this.close());

    // ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.close();
      }
    });

    // Quantity controls
    this.cartPage.addEventListener("click", (e) => {
      const target = e.target.closest("[data-action]");
      if (!target) return;

      const action = target.dataset.action;
      const itemKey = target.dataset.cartItemKey;

      if (action === "increase") {
        this.updateQuantity(itemKey, 1);
      } else if (action === "decrease") {
        this.updateQuantity(itemKey, -1);
      } else if (action === "remove") {
        this.removeItem(itemKey);
      }
    });

    // Quantity input changes
    this.cartPage.addEventListener("change", (e) => {
      if (e.target.classList.contains("quantity-value")) {
        const itemKey = e.target.dataset.cartItemKey;
        const quantity = parseInt(e.target.value);
        this.updateQuantity(itemKey, quantity, true);
      }
    });
  }

  close() {
    // Navigate back or close the page
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  }

  async updateQuantity(itemKey, change, isDirectInput = false) {
    const input = this.cartPage.querySelector(
      `[data-cart-item-key="${itemKey}"] .quantity-value`
    );
    if (!input) return;

    let newQuantity;
    if (isDirectInput) {
      newQuantity = change;
    } else {
      newQuantity = parseInt(input.value) + change;
    }

    if (newQuantity < 0) newQuantity = 0;

    try {
      this.isUpdatingFromPage = true;
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: newQuantity,
        }),
      });

      if (response.ok) {
        const cart = await response.json();
        this.updateCartDisplay(cart);

        // Redirect to home if cart is empty
        if (cart.item_count === 0) {
          setTimeout(() => {
            window.location.href = "/";
          }, 500);
        }
      }
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      this.isUpdatingFromPage = false;
    }
  }

  async removeItem(itemKey) {
    try {
      this.isUpdatingFromPage = true;
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0,
        }),
      });

      if (response.ok) {
        const cart = await response.json();
        this.updateCartDisplay(cart);

        // Redirect to home if cart is empty
        if (cart.item_count === 0) {
          setTimeout(() => {
            window.location.href = "/";
          }, 500);
        }
      }
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      this.isUpdatingFromPage = false;
    }
  }

  updateCartDisplay(cart) {
    // Update cart count in header
    const cartCountElements = document.querySelectorAll("[data-cart-count]");
    cartCountElements.forEach((el) => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? "inline" : "none";
    });

    // Hide/show cart button based on cart state
    const cartButtons = document.querySelectorAll(".cart-drawer-trigger");
    cartButtons.forEach((button) => {
      if (cart.item_count === 0) {
        button.style.display = "none";
      } else {
        button.style.display = "inline-flex";
      }
    });

    // Update cart page content
    this.updatePageContent(cart);

    // Dispatch cart updated event for other components
    if (!this.isUpdatingFromPage && !this.isUpdatingFromTrigger) {
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart, source: "cart-page" },
        })
      );
    }
  }

  updatePageContent(cart) {
    // Update the subtotal
    const subtotalElement = this.cartPage.querySelector(
      ".cart-page__subtotal-amount"
    );
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.total_price);
    }

    // Update cart items content
    const cartItemsContainer = this.cartPage.querySelector(".cart-page__items");
    const cartForm = this.cartPage.querySelector(".cart-page__form");
    const emptyState = this.cartPage.querySelector(".cart-page__empty");

    if (cart.item_count === 0) {
      // Show empty state
      if (cartForm) cartForm.style.display = "none";
      if (emptyState) emptyState.style.display = "block";
    } else {
      // Show cart items
      if (cartForm) cartForm.style.display = "block";
      if (emptyState) emptyState.style.display = "none";

      if (cartItemsContainer && cart.items) {
        // Clear existing items
        cartItemsContainer.innerHTML = "";

        // Add new items
        cart.items.forEach((item) => {
          const itemElement = this.createCartItemElement(item);
          cartItemsContainer.appendChild(itemElement);
        });
      }
    }
  }

  createCartItemElement(item) {
    const itemDiv = document.createElement("div");
    itemDiv.className = "cart-page__item";
    itemDiv.setAttribute("data-cart-item-key", item.key);

    // Determine if this is a bookshop product
    const isBookshop =
      item.product_type === "Book" ||
      (item.product &&
        item.product.collections &&
        item.product.collections.some((c) => c.handle === "book-shop"));

    // Determine if this is a workshop product
    const isWorkshop =
      item.product &&
      item.product.collections &&
      item.product.collections.some((c) => c.handle === "workshops");

    // Get category information
    let categoryHtml = "";
    if (item.product && item.product.collections) {
      const primaryCollection = item.product.collections.find(
        (c) =>
          c.handle === "book-shop" ||
          c.handle === "calendar" ||
          c.handle === "workshops"
      );
      if (primaryCollection) {
        categoryHtml = `<div class="cart-page__item-category">${primaryCollection.title}</div>`;
      }
    }

    // Get author information for bookshop products
    const authorHtml =
      isBookshop &&
      item.product &&
      item.product.metafields &&
      item.product.metafields.custom &&
      item.product.metafields.custom.author
        ? `<p class="cart-page__item-author">${item.product.metafields.custom.author}</p>`
        : "";

    const imageHtml = item.image
      ? `<img src="${item.image}" alt="${item.title}" loading="lazy" width="120" height="120" style="width: 100%; height: 100%; object-fit: contain;">`
      : `<div class="cart-page__item-placeholder"><svg width="60" height="60" viewBox="0 0 60 60" fill="none"><rect width="60" height="60" fill="#f5f5f5"/><path d="M20 25L30 35L40 25" stroke="#ccc" stroke-width="2"/></svg></div>`;

    const variantHtml = item.variant_title
      ? `<p class="cart-page__item-variant">${item.variant_title}</p>`
      : "";

    const originalPriceHtml =
      item.original_price !== item.final_price
        ? `<span class="cart-page__item-price-original">${
            item.original_price === 0
              ? "Free"
              : this.formatMoney(item.original_price)
          }</span>`
        : "";

    itemDiv.innerHTML = `
        <div class="cart-page__item-image">
          ${imageHtml}
        </div>
        <div class="cart-page__item-details">
          ${categoryHtml}
          <h3 class="cart-page__item-title${isBookshop ? " is-bookshop" : ""}">
            <a href="${item.url}">${item.title}</a>
          </h3>
          ${variantHtml}
          ${authorHtml}
          <div class="cart-page__item-price">
            ${originalPriceHtml}
            <span class="cart-page__item-price-final">${
              item.final_price === 0
                ? "Free"
                : this.formatMoney(item.final_price)
            }</span>
          </div>
        </div>
        <div class="cart-page__item-quantity">
          <div class="quantity-buttons">
            <button type="button" class="minus" data-action="decrease" data-cart-item-key="${
              item.key
            }" aria-label="Decrease quantity">–</button>
            <input type="number" name="updates[]" value="${
              item.quantity
            }" min="0" max="99" inputmode="numeric" class="quantity-value" data-cart-item-key="${
      item.key
    }">
            <button type="button" class="plus" data-action="increase" data-cart-item-key="${
              item.key
            }" aria-label="Increase quantity">+</button>
          </div>
          <button type="button" class="cart-page__remove-btn" data-action="remove" data-cart-item-key="${
            item.key
          }" aria-label="Remove">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;

    return itemDiv;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    })
      .format(cents / 100)
      .replace("$ ", "$");
  }
}

// Initialize cart page
document.addEventListener("DOMContentLoaded", () => {
  window.cartPage = new CartPage();
});

// Listen for cart updates from other parts of the site
document.addEventListener("cart:updated", (event) => {
  // Don't process events that originated from the cart page itself
  if (event.detail.source === "cart-page") {
    return;
  }

  if (window.cartPage && event.detail.cart) {
    window.cartPage.updateCartDisplay(event.detail.cart);
  }
});
